import { createClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database.types'
import type { SupabaseClient } from '@supabase/supabase-js'
import { useParagonStore } from '@/stores/useParagonStore'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    '[PHYRE] Missing Supabase credentials. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env'
  )
}

/**
 * Pre-configured Supabase client for the PHYRE platform.
 *
 * Reads credentials from `VITE_SUPABASE_URL` and `VITE_SUPABASE_ANON_KEY`
 * environment variables. Falls back to localhost defaults when missing
 * (local dev without Supabase running).
 *
 * @example
 * ```ts
 * import { supabase } from '@/lib/supabase'
 * const { data } = await supabase.from('tenants').select('*')
 * ```
 */
export const supabase = createClient<Database>(
  supabaseUrl || 'http://localhost:54321',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
    global: {
      headers: {
        'x-application': 'phyre-origin',
      },
    },
  }
)

/**
 * PARAGON Impersonation Client
 *
 * Returns a Supabase client configured for the current auth context.
 * When a PARAGON agency admin is actively impersonating a tenant, this
 * returns a client using the impersonation JWT so that RLS policies
 * resolve to the target tenant. Otherwise returns the default supabase client.
 *
 * @returns Supabase client scoped to the active impersonation token (if any)
 *
 * @example
 * ```ts
 * import { getActiveSupabaseClient } from '@/lib/supabase'
 * const client = getActiveSupabaseClient()
 * const { data } = await client.from('canvas_layouts').select('*')
 * ```
 */
let _impersonationClient: SupabaseClient<Database> | null = null
let _lastImpersonationToken: string | null = null

export function getActiveSupabaseClient(): SupabaseClient<Database> {
  const { impersonationToken } = useParagonStore.getState()

  if (!impersonationToken) {
    // Not impersonating — return the default client
    _impersonationClient = null
    _lastImpersonationToken = null
    return supabase
  }

  // Reuse cached client if token hasn't changed
  if (impersonationToken === _lastImpersonationToken && _impersonationClient) {
    return _impersonationClient
  }

  // Create a new client with the impersonation token
  _lastImpersonationToken = impersonationToken
  _impersonationClient = createClient<Database>(
    supabaseUrl || 'http://localhost:54321',
    supabaseAnonKey || 'placeholder-key',
    {
      global: {
        headers: {
          'x-application': 'phyre-origin',
          Authorization: `Bearer ${impersonationToken}`,
        },
      },
      auth: {
        autoRefreshToken: false,
        persistSession: false,
      },
    }
  )

  return _impersonationClient
}
